import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, Settings2, Type, Hash, Calendar as CalendarIcon, List, CheckSquare, MoreHorizontal, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const FIELD_TYPES = [
  { value: "text", label: "Text", icon: Type, color: "text-blue-500" },
  { value: "number", label: "Number", icon: Hash, color: "text-emerald-500" },
  { value: "date", label: "Date", icon: CalendarIcon, color: "text-amber-500" },
  { value: "dropdown", label: "Dropdown", icon: List, color: "text-purple-500" },
  { value: "checkbox", label: "Checkbox", icon: CheckSquare, color: "text-pink-500" }
];

export const CustomFieldsView = ({ customFields, projects, onCreateField, onUpdateField, onDeleteField }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newField, setNewField] = useState({ name: "", field_type: "text", project_id: "", options: [], required: false });
  const [newOption, setNewOption] = useState("");

  const handleCreate = () => {
    if (!newField.name.trim()) { toast.error("Field name is required"); return; }
    const data = { ...newField };
    if (!data.project_id) delete data.project_id;
    if (data.field_type !== "dropdown") data.options = [];
    onCreateField(data);
    setNewField({ name: "", field_type: "text", project_id: "", options: [], required: false });
    setShowCreateModal(false);
  };

  const addOption = () => {
    if (newOption.trim() && !newField.options.includes(newOption.trim())) {
      setNewField({ ...newField, options: [...newField.options, newOption.trim()] });
      setNewOption("");
    }
  };

  const removeOption = (opt) => {
    setNewField({ ...newField, options: newField.options.filter(o => o !== opt) });
  };

  const getFieldTypeConfig = (type) => FIELD_TYPES.find(ft => ft.value === type) || FIELD_TYPES[0];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2" data-testid="custom-fields-heading">
          <Settings2 className="h-5 w-5 text-primary" />Custom Fields
        </h2>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-custom-field-btn">
          <Plus className="h-4 w-4 mr-2" />New Field
        </Button>
      </div>

      {customFields.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed">
          <Settings2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No custom fields yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Create custom fields to track additional information on your tasks</p>
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />Create Field
          </Button>
        </div>
      ) : (
        <div className="space-y-3 max-w-2xl">
          {customFields.map(field => {
            const config = getFieldTypeConfig(field.field_type);
            const IconComp = config.icon;
            const project = projects.find(p => p.id === field.project_id);

            return (
              <div key={field.id} className="flex items-center gap-4 p-4 bg-card rounded-xl border group hover:border-primary/30 transition-colors" data-testid={`custom-field-${field.id}`}>
                <div className={`p-2.5 rounded-lg bg-muted`}>
                  <IconComp className={`h-5 w-5 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm">{field.name}</h4>
                    {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">{config.label}</Badge>
                    {project && <Badge variant="outline" className="text-xs">{project.name}</Badge>}
                    {!field.project_id && <Badge variant="outline" className="text-xs text-muted-foreground">Global</Badge>}
                    {field.field_type === "dropdown" && field.options?.length > 0 && (
                      <span className="text-xs text-muted-foreground">{field.options.length} options</span>
                    )}
                  </div>
                  {field.field_type === "dropdown" && field.options?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {field.options.map(opt => (
                        <Badge key={opt} variant="secondary" className="text-xs">{opt}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onDeleteField(field.id)} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />Create Custom Field
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Field Name</label>
              <Input
                placeholder="e.g., Story Points, Sprint, Client"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                data-testid="custom-field-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Field Type</label>
              <div className="grid grid-cols-5 gap-2">
                {FIELD_TYPES.map(ft => {
                  const Icon = ft.icon;
                  return (
                    <button
                      key={ft.value}
                      onClick={() => setNewField({ ...newField, field_type: ft.value })}
                      className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1.5 transition-all ${newField.field_type === ft.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                      data-testid={`field-type-${ft.value}`}
                    >
                      <Icon className={`h-5 w-5 ${ft.color}`} />
                      <span className="text-xs font-medium">{ft.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {newField.field_type === "dropdown" && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Options</label>
                <div className="space-y-2">
                  {newField.options.map(opt => (
                    <div key={opt} className="flex items-center gap-2">
                      <Badge variant="secondary" className="flex-1">{opt}</Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeOption(opt)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add option..."
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
                      className="h-8 text-sm"
                    />
                    <Button type="button" size="sm" variant="outline" onClick={addOption} className="h-8">Add</Button>
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Project (optional)</label>
              <Select value={newField.project_id || "global"} onValueChange={(v) => setNewField({ ...newField, project_id: v === "global" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">All projects (global)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Required field</span>
              <Switch checked={newField.required} onCheckedChange={(checked) => setNewField({ ...newField, required: checked })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-custom-field-btn">Create Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
